import { useEffect, useState, useRef, useCallback } from "react";
import AppConst from "../apis/appconst";
import axios from "axios";
import { toast } from "react-toastify";
import Select from "react-select";

// ── Available template variables that can be placed on the image ──
const AVAILABLE_VARIABLES = [
  { key: "patient_name",      label: "Patient Name",     sample: "Mr. Rahul Sharma" },
  { key: "patient_uhid",      label: "UHID",             sample: "SH00100123" },
  { key: "patient_age",       label: "Age",              sample: "25 Years 3 Months" },
  { key: "patient_gender",    label: "Gender",           sample: "Male" },
  { key: "patient_mobile",    label: "Mobile",           sample: "9876543210" },
  { key: "patient_address",   label: "Address",          sample: "123 Main St, Delhi" },
  { key: "guardian_name",     label: "Guardian Name",    sample: "C/O Ram Kumar" },
  { key: "doctor_name",       label: "Doctor Name",      sample: "Dr. Amit Singh" },
  { key: "doctor_degree",     label: "Doctor Degree",    sample: "MBBS, MD" },
  { key: "doctor_speciality", label: "Doctor Speciality",sample: "Cardiology" },
  { key: "doctor_reg_no",     label: "Doctor Reg No",    sample: "DMC-12345" },
  { key: "opd_date",          label: "OPD Date",         sample: "24-Jun-2026 10:30 AM" },
  { key: "token_no",          label: "Token No",         sample: "5" },
  { key: "opd_id",            label: "OPD No",           sample: "00045" },
  { key: "opd_fees",          label: "OPD Fees",         sample: "500.00" },
  { key: "reg_fees",          label: "Registration Fees", sample: "100.00" },
  { key: "total_amount",      label: "Total Amount",     sample: "600.00" },
  { key: "discount",          label: "Discount (%)",     sample: "10" },
  { key: "discount_amt",      label: "Discount (₹)",     sample: "60.00" },
  { key: "final_amount",      label: "Final Amount",     sample: "540.00" },
  { key: "paid",              label: "Paid",             sample: "540.00" },
  { key: "due",               label: "Due",              sample: "0.00" },
  { key: "payment_mode",      label: "Payment Mode",     sample: "Cash" },
  { key: "remark",            label: "Remark",           sample: "Follow up after 7 days" },
  { key: "amount_in_words",   label: "Amount in Words",  sample: "Five Hundred Forty Only" },
];

function OpdFormat() {
  // ── List state ──
  const [formats, setFormats] = useState([]);
  const [loading, setLoading] = useState(false);

  // ── Speciality list ──
  const [specialities, setSpecialities] = useState([]);

  // ── Form state ──
  const [editingId, setEditingId] = useState(null);
  const [selectedSpeciality, setSelectedSpeciality] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImage, setExistingImage] = useState("");
  const [variables, setVariables] = useState([]); // [{key, label, x, y, fontSize, fontColor, fontWeight}]
  const [pageSize, setPageSize] = useState("a4");
  const [orientation, setOrientation] = useState("portrait");
  const [saving, setSaving] = useState(false);

  // ── Drag state ──
  const [draggingIdx, setDraggingIdx] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const canvasRef = useRef(null);
  const imgRef = useRef(null);

  // ── Image natural dimensions ──
  const [imgNatural, setImgNatural] = useState({ w: 595, h: 842 }); // A4 default

  // ══════════════════════════════════════════════════════
  //  INIT: Load specialities + existing formats
  // ══════════════════════════════════════════════════════
  useEffect(() => {
    loadSpecialities();
    loadFormats();
  }, []);

  const loadSpecialities = async () => {
    try {
      const fd = new FormData();
      fd.append("cat_type", "speciality");
      fd.append("page_no", "none");
      fd.append("par_by", "1");
      const res = await axios.post(AppConst.BASE_URL + AppConst.getCatListUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.status) setSpecialities(res.data.records || []);
    } catch (err) {
      console.error("loadSpecialities error:", err);
    }
  };

  const loadFormats = async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      const res = await axios.post(AppConst.BASE_URL + AppConst.getOpdFormatsUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.status) setFormats(res.data.records || []);
      else setFormats([]);
    } catch (err) {
      console.error("loadFormats error:", err);
    } finally {
      setLoading(false);
    }
  };

  // ══════════════════════════════════════════════════════
  //  FORM HANDLERS
  // ══════════════════════════════════════════════════════
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleImgLoad = (e) => {
    const img = e.target;
    setImgNatural({ w: img.naturalWidth, h: img.naturalHeight });
  };

  const addVariable = (varDef) => {
    // Don't add duplicate
    if (variables.find((v) => v.key === varDef.key)) {
      toast.warning(`"${varDef.label}" already added`);
      return;
    }
    setVariables((prev) => [
      ...prev,
      {
        key: varDef.key,
        label: varDef.label,
        sample: varDef.sample,
        x: 10,       // percentage from left
        y: 10 + prev.length * 5, // stagger
        fontSize: 12,
        fontColor: "#000000",
        fontWeight: "normal",
      },
    ]);
  };

  const removeVariable = (idx) => {
    setVariables((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateVariable = (idx, field, value) => {
    setVariables((prev) =>
      prev.map((v, i) => (i === idx ? { ...v, [field]: value } : v))
    );
  };

  // ── Drag handlers (percentage-based positions) ──
  const getCanvasRect = () => {
    if (!canvasRef.current) return null;
    return canvasRef.current.getBoundingClientRect();
  };

  const handleMouseDown = (idx, e) => {
    e.preventDefault();
    const rect = getCanvasRect();
    if (!rect) return;
    const currentX = (variables[idx].x / 100) * rect.width;
    const currentY = (variables[idx].y / 100) * rect.height;
    setDraggingIdx(idx);
    setDragOffset({
      x: e.clientX - rect.left - currentX,
      y: e.clientY - rect.top - currentY,
    });
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (draggingIdx === null) return;
      const rect = getCanvasRect();
      if (!rect) return;
      const rawX = e.clientX - rect.left - dragOffset.x;
      const rawY = e.clientY - rect.top - dragOffset.y;
      const pctX = Math.max(0, Math.min(95, (rawX / rect.width) * 100));
      const pctY = Math.max(0, Math.min(95, (rawY / rect.height) * 100));
      updateVariable(draggingIdx, "x", parseFloat(pctX.toFixed(2)));
      // also set y
      setVariables((prev) =>
        prev.map((v, i) =>
          i === draggingIdx ? { ...v, x: parseFloat(pctX.toFixed(2)), y: parseFloat(pctY.toFixed(2)) } : v
        )
      );
    },
    [draggingIdx, dragOffset]
  );

  const handleMouseUp = useCallback(() => {
    setDraggingIdx(null);
  }, []);

  useEffect(() => {
    if (draggingIdx !== null) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [draggingIdx, handleMouseMove, handleMouseUp]);

  // ── SAVE ──
  const handleSave = async () => {
    if (!selectedSpeciality) {
      toast.error("Please select a speciality");
      return;
    }
    if (!imageFile && !existingImage) {
      toast.error("Please upload a format image");
      return;
    }

    setSaving(true);
    try {
      const fd = new FormData();
      if (editingId) fd.append("format_id", editingId);
      fd.append("format_speciality_id", selectedSpeciality.value);
      fd.append("format_speciality_name", selectedSpeciality.label);
      fd.append("format_variables", JSON.stringify(variables));
      fd.append("format_page_size", pageSize);
      fd.append("format_orientation", orientation);
      fd.append("format_status", "active");
      fd.append("created_by", "1");
      if (imageFile) fd.append("format_image_file", imageFile);

      const res = await axios.post(AppConst.BASE_URL + AppConst.saveOpdFormatUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.status) {
        toast.success(res.data.success || "Saved");
        resetForm();
        loadFormats();
      } else {
        toast.error(res.data.success || "Failed");
      }
    } catch (err) {
      toast.error("Server error");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // ── EDIT ──
  const handleEdit = (fmt) => {
    setEditingId(fmt.format_id);
    setSelectedSpeciality({
      value: fmt.format_speciality_id,
      label: fmt.format_speciality_name,
    });
    setExistingImage(fmt.format_image || "");
    setImagePreview(fmt.format_image ? AppConst.BASE_URL + "/" + fmt.format_image : null);
    setImageFile(null);
    setPageSize(fmt.format_page_size || "a4");
    setOrientation(fmt.format_orientation || "portrait");

    try {
      const parsed = JSON.parse(fmt.format_variables || "[]");
      setVariables(parsed);
    } catch {
      setVariables([]);
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── DELETE ──
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this format?")) return;
    try {
      const fd = new FormData();
      fd.append("format_id", id);
      const res = await axios.post(AppConst.BASE_URL + AppConst.deleteOpdFormatUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.status) {
        toast.success("Deleted");
        loadFormats();
      }
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  // ── RESET ──
  const resetForm = () => {
    setEditingId(null);
    setSelectedSpeciality(null);
    setImageFile(null);
    setImagePreview(null);
    setExistingImage("");
    setVariables([]);
    setPageSize("a4");
    setOrientation("portrait");
  };

  const specOptions = [
    { value: "all", label: "All Specialities (Default)" },
    ...specialities.map((s) => ({
      value: s.cat_id,
      label: s.cat_name,
    })),
  ];

  const currentImgSrc = imagePreview || (existingImage ? AppConst.BASE_URL + "/" + existingImage : null);

  // ══════════════════════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════════════════════
  return (
    <div className="page-content">
      <div className="container-fluid">
        <h2 style={{ color: "#405189" }}>OPD Format Manager</h2>
        <p className="text-muted">
          Upload image templates per speciality and place patient detail variables on them.
          When printing OPD receipts, the matching speciality format will be used automatically.
        </p>

        {/* ════════════ FORM CARD ════════════ */}
        <div className="card">
          <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              {editingId ? "Edit Format" : "Add New Format"}
            </h5>
            {editingId && (
              <button className="btn btn-light btn-sm" onClick={resetForm}>
                Cancel Edit
              </button>
            )}
          </div>
          <div className="card-body">
            <div className="row g-3">
              {/* Speciality */}
              <div className="col-md-4">
                <label className="form-label fw-bold">Speciality *</label>
                <Select
                  options={specOptions}
                  value={selectedSpeciality}
                  onChange={setSelectedSpeciality}
                  placeholder="Select speciality..."
                  isClearable
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
                />
              </div>

              {/* Page Size */}
              <div className="col-md-2">
                <label className="form-label fw-bold">Page Size</label>
                <select className="form-control" value={pageSize} onChange={(e) => setPageSize(e.target.value)}>
                  <option value="a4">A4</option>
                  <option value="a5">A5</option>
                  <option value="letter">Letter</option>
                </select>
              </div>

              {/* Orientation */}
              <div className="col-md-2">
                <label className="form-label fw-bold">Orientation</label>
                <select className="form-control" value={orientation} onChange={(e) => setOrientation(e.target.value)}>
                  <option value="portrait">Portrait</option>
                  <option value="landscape">Landscape</option>
                </select>
              </div>

              {/* Image Upload */}
              <div className="col-md-4">
                <label className="form-label fw-bold">Format Image *</label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                <small className="text-muted">Upload the template/letterhead image (JPG/PNG)</small>
              </div>
            </div>

            {/* ════════════ IMAGE CANVAS + VARIABLES ════════════ */}
            {currentImgSrc && (
              <div className="row mt-4">
                {/* Left: Variable palette */}
                <div className="col-md-3">
                  <div className="card border">
                    <div className="card-header py-2" style={{ background: "#f0f4ff" }}>
                      <strong style={{ fontSize: 13 }}>Available Variables</strong>
                    </div>
                    <div className="card-body p-2" style={{ maxHeight: 500, overflowY: "auto" }}>
                      {AVAILABLE_VARIABLES.map((v) => {
                        const alreadyAdded = variables.some((va) => va.key === v.key);
                        return (
                          <div
                            key={v.key}
                            className="d-flex justify-content-between align-items-center mb-1 p-2 rounded"
                            style={{
                              background: alreadyAdded ? "#d1e7dd" : "#fff",
                              border: "1px solid #dee2e6",
                              fontSize: 12,
                              cursor: alreadyAdded ? "default" : "pointer",
                            }}
                            onClick={() => !alreadyAdded && addVariable(v)}
                          >
                            <span>
                              {alreadyAdded && <i className="ri-check-line text-success me-1" />}
                              {v.label}
                            </span>
                            {!alreadyAdded && (
                              <i className="ri-add-circle-line text-primary" style={{ fontSize: 16 }} />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Center: Image canvas with draggable variables */}
                <div className="col-md-6">
                  <div className="card border">
                    <div className="card-header py-2 d-flex justify-content-between" style={{ background: "#f0f4ff" }}>
                      <strong style={{ fontSize: 13 }}>
                        Preview — drag variables to position them
                      </strong>
                      <small className="text-muted">Click a variable from the left to add</small>
                    </div>
                    <div className="card-body p-2">
                      <div
                        ref={canvasRef}
                        style={{
                          position: "relative",
                          width: "100%",
                          border: "2px solid #405189",
                          borderRadius: 4,
                          overflow: "hidden",
                          userSelect: "none",
                          cursor: draggingIdx !== null ? "grabbing" : "default",
                        }}
                      >
                        <img
                          ref={imgRef}
                          src={currentImgSrc}
                          alt="Format Template"
                          onLoad={handleImgLoad}
                          style={{
                            width: "100%",
                            display: "block",
                            pointerEvents: "none",
                          }}
                          draggable={false}
                        />

                        {/* Render variable tags on the image */}
                        {variables.map((v, idx) => (
                          <div
                            key={v.key}
                            style={{
                              position: "absolute",
                              left: `${v.x}%`,
                              top: `${v.y}%`,
                              background:
                                draggingIdx === idx
                                  ? "rgba(64,81,137,0.9)"
                                  : "rgba(64,81,137,0.75)",
                              color: "#fff",
                              padding: "2px 8px",
                              borderRadius: 3,
                              fontSize: 11,
                              fontWeight: 600,
                              cursor: "grab",
                              whiteSpace: "nowrap",
                              zIndex: draggingIdx === idx ? 100 : 10,
                              boxShadow: draggingIdx === idx ? "0 2px 8px rgba(0,0,0,0.3)" : "none",
                              transform: "translate(0, -50%)",
                            }}
                            onMouseDown={(e) => handleMouseDown(idx, e)}
                          >
                            {`{${v.label}}`}
                            <span
                              style={{
                                marginLeft: 6,
                                cursor: "pointer",
                                color: "#ff9999",
                                fontWeight: 400,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                removeVariable(idx);
                              }}
                            >
                              ×
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right: Variable properties */}
                <div className="col-md-3">
                  <div className="card border">
                    <div className="card-header py-2" style={{ background: "#f0f4ff" }}>
                      <strong style={{ fontSize: 13 }}>
                        Placed Variables ({variables.length})
                      </strong>
                    </div>
                    <div className="card-body p-2" style={{ maxHeight: 500, overflowY: "auto" }}>
                      {variables.length === 0 && (
                        <p className="text-muted text-center" style={{ fontSize: 12 }}>
                          No variables added yet. Click from the left panel to add.
                        </p>
                      )}
                      {variables.map((v, idx) => (
                        <div
                          key={v.key}
                          className="mb-2 p-2 rounded"
                          style={{ border: "1px solid #dee2e6", fontSize: 11 }}
                        >
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <strong style={{ color: "#405189" }}>{v.label}</strong>
                            <button
                              type="button"
                              className="btn btn-link btn-sm text-danger p-0"
                              onClick={() => removeVariable(idx)}
                            >
                              <i className="ri-delete-bin-line" />
                            </button>
                          </div>
                          <div className="row g-1">
                            <div className="col-6">
                              <label style={{ fontSize: 10 }}>X (%)</label>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={v.x}
                                min={0}
                                max={100}
                                step={0.5}
                                onChange={(e) => updateVariable(idx, "x", parseFloat(e.target.value) || 0)}
                              />
                            </div>
                            <div className="col-6">
                              <label style={{ fontSize: 10 }}>Y (%)</label>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={v.y}
                                min={0}
                                max={100}
                                step={0.5}
                                onChange={(e) => updateVariable(idx, "y", parseFloat(e.target.value) || 0)}
                              />
                            </div>
                            <div className="col-4">
                              <label style={{ fontSize: 10 }}>Size</label>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={v.fontSize}
                                min={6}
                                max={36}
                                onChange={(e) => updateVariable(idx, "fontSize", parseInt(e.target.value) || 12)}
                              />
                            </div>
                            <div className="col-4">
                              <label style={{ fontSize: 10 }}>Color</label>
                              <input
                                type="color"
                                className="form-control form-control-sm p-0"
                                value={v.fontColor}
                                onChange={(e) => updateVariable(idx, "fontColor", e.target.value)}
                                style={{ height: 28 }}
                              />
                            </div>
                            <div className="col-4">
                              <label style={{ fontSize: 10 }}>Weight</label>
                              <select
                                className="form-control form-control-sm"
                                value={v.fontWeight}
                                onChange={(e) => updateVariable(idx, "fontWeight", e.target.value)}
                              >
                                <option value="normal">Normal</option>
                                <option value="bold">Bold</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Save Button */}
            <div className="text-end mt-3">
              <button className="btn btn-primary px-4" onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" />
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line me-1" />
                    {editingId ? "Update Format" : "Save Format"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* ════════════ LIST CARD ════════════ */}
        <div className="card">
          <div className="card-header">
            <h5 style={{ color: "#405189" }} className="mb-0">
              Saved OPD Formats
            </h5>
          </div>
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-4">
                <span className="spinner-border text-primary" />
              </div>
            ) : formats.length === 0 ? (
              <div className="text-center py-4 text-muted">
                No formats added yet. Create one above.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 50 }}>#</th>
                      <th>Speciality</th>
                      <th>Preview</th>
                      <th>Variables</th>
                      <th>Page</th>
                      <th>Status</th>
                      <th style={{ width: 160 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formats.map((fmt, idx) => {
                      let varCount = 0;
                      try {
                        varCount = JSON.parse(fmt.format_variables || "[]").length;
                      } catch {}
                      return (
                        <tr key={fmt.format_id}>
                          <td>{idx + 1}</td>
                          <td>
                            <strong style={{ color: "#405189" }}>
                              {fmt.format_speciality_name || `ID: ${fmt.format_speciality_id}`}
                            </strong>
                          </td>
                          <td>
                            {fmt.format_image ? (
                              <img
                                src={AppConst.BASE_URL + "/" + fmt.format_image}
                                alt="fmt"
                                style={{
                                  height: 60,
                                  borderRadius: 4,
                                  border: "1px solid #dee2e6",
                                  cursor: "pointer",
                                }}
                                onClick={() => window.open(AppConst.BASE_URL + "/" + fmt.format_image, "_blank")}
                              />
                            ) : (
                              <span className="text-muted">No image</span>
                            )}
                          </td>
                          <td>
                            <span className="badge bg-info">{varCount} variables</span>
                          </td>
                          <td>
                            {(fmt.format_page_size || "a4").toUpperCase()} / {fmt.format_orientation || "portrait"}
                          </td>
                          <td>
                            <span className={`badge ${fmt.format_status === "active" ? "bg-success" : "bg-secondary"}`}>
                              {fmt.format_status}
                            </span>
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-primary me-1"
                              onClick={() => handleEdit(fmt)}
                            >
                              <i className="ri-edit-line" /> Edit
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(fmt.format_id)}
                            >
                              <i className="ri-delete-bin-line" /> Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OpdFormat;